import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { Vehicle, VehicleListItem, PaginatedVehicles } from '@/types/vehicle'
import type { VehicleFormData } from '@/validators/vehicle'

export interface ListVehicleParams {
  search?: string
  active?: boolean
  vehicleType?: string
  page?: number
  perPage?: number
}

const KEYS = {
  list: (params: ListVehicleParams) => ['vehicles', params] as const,
  detail: (id: string) => ['vehicles', id] as const,
}

export function useVehicles(params: ListVehicleParams = {}) {
  return useQuery({
    queryKey: KEYS.list(params),
    queryFn: () =>
      api.get<PaginatedVehicles>('/vehicles', { params }).then((r) => r.data),
  })
}

export function useVehicle(id: string) {
  return useQuery({
    queryKey: KEYS.detail(id),
    queryFn: () => api.get<Vehicle>(`/vehicles/${id}`).then((r) => r.data),
    enabled: !!id,
  })
}

export function useCreateVehicle() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: VehicleFormData) =>
      api.post<Vehicle>('/vehicles', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vehicles'] }),
  })
}

export function useUpdateVehicle(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: VehicleFormData) =>
      api.put<Vehicle>(`/vehicles/${id}`, data).then((r) => r.data),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ['vehicles'] })
      qc.setQueryData(KEYS.detail(id), updated)
    },
  })
}

export function useDeactivateVehicle() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api.patch<VehicleListItem>(`/vehicles/${id}/deactivate`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vehicles'] }),
  })
}

export function useActivateVehicle() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api.patch<VehicleListItem>(`/vehicles/${id}/activate`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vehicles'] }),
  })
}
